import React, { useState } from 'react'
import { SizableText, styled, XStack, YStack } from '@mythical/ui'
import { GifOfTheDay } from 'app/blocks/homepage/gif-of-the-day'
import Section from 'app/components/structure/Section'
import { Button } from 'app/components/primitive/Button'
import { IconHeart } from 'app/components/primitive/Icon'
import { IconHeartFull } from 'app/components/primitive/Icon/icons/IconHeartFull'
import { IconShare } from 'app/components/primitive/Icon/icons/IconShare'
import ShareModal from 'app/components/content/ShareModal/ShareModal'
import useRequester from 'app/hooks/use-requester'
import {
  toggleGifLike,
  toggleGifLikeResult,
} from 'app/mutations/gif/toggle-like'
import { SolitoImage } from 'solito/image'
import { useStores } from 'app/store'
import useAnalytics from 'app/lib/analytics'

const GifWrapper = styled(YStack, {
  pos: 'relative',
  fs: 0,
  aspectRatio: 16 / 9,
  br: '$6',
  ov: 'hidden',
  $gtSm: {
    h: 220,
  },
})

export const GifOfTheDayBlock: React.FC<{ block: GifOfTheDay }> = ({
  block,
}) => {
  const { gif, hasGif, isFallback } = block
  const { request } = useRequester()
  const { userStore } = useStores()
  const analytics = useAnalytics()

  const [likes, setLikes] = useState(gif?.likes ?? 0)
  const [hasLiked, setHasLiked] = useState(gif?.hasLiked ?? false)
  const [isLoading, setIsLoading] = useState(false)

  const isLoggedIn = !!userStore?.user?.id

  // Disable liking for fallback GIFs (they have no database record to attach likes to)
  const canLike = isLoggedIn && !isFallback

  if (!hasGif || !gif) {
    return null
  }

  const imageUrl = gif.gif?.url

  if (!imageUrl) {
    return null
  }

  const handleLike = async () => {
    if (!canLike || isLoading) return

    setIsLoading(true)

    // Optimistic update
    const previousLikes = likes
    const previousHasLiked = hasLiked
    setLikes(hasLiked ? likes - 1 : likes + 1)
    setHasLiked(!hasLiked)

    try {
      const result = await request(
        toggleGifLike(),
        { id: gif.id },
        toggleGifLikeResult,
      )

      if (result?.success) {
        setLikes(result.likes)
        setHasLiked(result.hasLiked)
        analytics.logEvent('gif_of_the_day_like', {
          gif_id: gif.id,
          action: result.hasLiked ? 'like' : 'unlike',
        })
      } else {
        setLikes(previousLikes)
        setHasLiked(previousHasLiked)
      }
    } catch (error) {
      console.error('Failed to toggle like:', error)
      setLikes(previousLikes)
      setHasLiked(previousHasLiked)
    } finally {
      setIsLoading(false)
    }
  }

  const shareUrl = imageUrl

  return (
    <Section paddedX>
      <YStack
        bg="$brandOlive"
        br="$6"
        gap="$3.5"
        $gtSm={{ fd: 'row', jc: 'space-between' }}
        p="$3.5"
      >
        <YStack f={1} gap="$3.5" jc="space-between">
          <YStack gap="$2">
            <SizableText size="$7.5" fow="600">
              Gif of the Day
            </SizableText>
            <SizableText size="$2" $gtSm={{ size: '$4' }}>
              Today's most replay-worthy Mythical moment.
            </SizableText>
          </YStack>

          <GifWrapper $gtSm={{ display: 'none' }}>
            <SolitoImage
              src={imageUrl}
              alt="Gif of the Day"
              fill
              contentFit="cover"
              unoptimized
            />
          </GifWrapper>

          <XStack gap="$2">
            <Button
              theme="secondary"
              size="$3"
              $gtSm={{ size: '$4' }}
              onPress={handleLike}
              disabled={!canLike}
              icon={
                hasLiked ? (
                  <IconHeartFull size={20} color="$fgPrimary" />
                ) : (
                  <IconHeart size={20} color="$fgPrimary" />
                )
              }
            >
              {likes.toLocaleString()}
            </Button>
            <ShareModal
              href={shareUrl}
              trigger={
                <Button
                  theme="secondary"
                  size="$3"
                  $gtSm={{ size: '$4' }}
                  icon={<IconShare size={20} color="$fgPrimary" />}
                >
                  Share
                </Button>
              }
            />
          </XStack>
        </YStack>

        <GifWrapper display="none" $gtSm={{ display: 'flex' }}>
          <SolitoImage
            src={imageUrl}
            alt="Gif of the Day"
            fill
            contentFit="cover"
            unoptimized
          />
        </GifWrapper>
      </YStack>
    </Section>
  )
}
